// NeighboringStars.js — "Also shining nearby."
// A dark celestial band on every memorial page showing the souls whose stars
// rest closest to this one in the galaxy — each a single click from their
// memorial. Turns every memorial into a doorway to others, the way a walk
// through a cemetery lets you notice the neighboring stones.

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const yearOf = (d) => {
  if (!d) return null;
  const date = d.seconds ? new Date(d.seconds * 1000) : new Date(d);
  return Number.isNaN(date.getTime()) ? null : date.getFullYear();
};

function NeighboringStars({ memorial }) {
  const [claims, setClaims] = useState(null);

  const starId = memorial?.galaxyLocation?.starId;
  const gx = memorial?.galaxyLocation?.x;
  const gy = memorial?.galaxyLocation?.y;
  const gz = memorial?.galaxyLocation?.z;

  useEffect(() => {
    if (!starId) return undefined;
    let cancelled = false;
    getDocs(collection(db, 'claimedStars'))
      .then((snap) => {
        if (!cancelled) setClaims(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((e) => {
        console.warn('NeighboringStars load failed:', e);
        if (!cancelled) setClaims([]);
      });
    return () => { cancelled = true; };
  }, [starId]);

  // Their constellation: stars claimed by the same steward — family, bound
  // together in the sky.
  const family = useMemo(() => {
    if (!claims || !memorial?.createdBy) return [];
    return claims.filter(
      (c) => c.id !== starId && c.memorialSlug && c.claimedBy === memorial.createdBy
    );
  }, [claims, starId, memorial]);

  const neighbors = useMemo(() => {
    if (!claims || typeof gx !== 'number') return [];
    const familyIds = new Set(family.map((f) => f.id));
    return claims
      .filter((c) => c.id !== starId && c.memorialSlug && !familyIds.has(c.id))
      .map((c) => ({
        ...c,
        d2: (c.x - gx) ** 2 + (c.y - gy) ** 2 + (c.z - gz) ** 2,
      }))
      .sort((a, b) => a.d2 - b.d2)
      .slice(0, 4);
  }, [claims, starId, gx, gy, gz, family]);

  // Nothing to show without a star or without anyone else shining
  if (!starId || (neighbors.length === 0 && family.length === 0)) return null;

  const SoulCard = ({ n }) => (
    <Link
      key={n.id}
      to={`/memorial/${n.memorialSlug}`}
      className="group w-36 md:w-40 p-4 rounded-2xl bg-[#fdfbf6] border border-[#eae6dd] hover:border-[#d8c9a4] hover:shadow-[0_4px_18px_rgba(184,150,79,0.12)] transition-all duration-300"
      style={{ textDecoration: 'none' }}
    >
      <div
        className="w-12 h-12 mx-auto rounded-full flex items-center justify-center text-lg group-hover:scale-105 transition-transform duration-300"
        style={{
          background: 'radial-gradient(circle at 35% 30%, #2a3152, #0d1020)',
          boxShadow: '0 0 0 1px rgba(203,171,106,0.5), 0 0 18px rgba(255,215,140,0.2)',
          color: '#ffe2a8',
        }}
      >
        ✦
      </div>
      <p className="mt-3 text-gray-800 text-sm font-light leading-snug">
        {n.firstName} {n.lastName}
      </p>
      {yearOf(n.birthDate) && yearOf(n.deathDate) && (
        <p className="text-gray-400 text-xs mt-0.5" style={{ letterSpacing: '0.08em' }}>
          {yearOf(n.birthDate)}–{yearOf(n.deathDate)}
        </p>
      )}
      <p className="mt-1.5 text-[10px] uppercase truncate" style={{ letterSpacing: '0.16em', color: '#b8964f' }}>
        ✦ {n.starName}
      </p>
    </Link>
  );

  return (
    <div className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df] text-center">
      <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#b8964f' }}>
        The Galaxy of Memorials
      </p>
      <h2 className="mt-3 text-2xl md:text-3xl font-extralight text-gray-900" style={{ letterSpacing: '0.04em' }}>
        {family.length > 0 ? 'Their Constellation' : 'Also Shining Nearby'}
      </h2>
      <div
        className="mx-auto mt-4 mb-8"
        style={{ width: '54px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}
      />

      {/* Family constellation — souls bound to this one */}
      {family.length > 0 && (
        <>
          <p className="text-gray-500 text-xs font-light mb-4" style={{ letterSpacing: '0.12em' }}>
            Memorials whose stars are bound to {memorial.firstName}'s — connected by golden threads in the galaxy
          </p>
          <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto mb-10">
            {family.map((n) => <SoulCard key={n.id} n={n} />)}
          </div>
        </>
      )}

      {/* Nearest other souls */}
      {neighbors.length > 0 && (
        <>
          {family.length > 0 && (
            <p className="text-[10px] uppercase text-gray-400 mb-4" style={{ letterSpacing: '0.28em' }}>
              Also Shining Nearby
            </p>
          )}
          <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
            {neighbors.map((n) => <SoulCard key={n.id} n={n} />)}
          </div>
        </>
      )}

      <Link
        to={`/galaxy?star=${starId}`}
        className="inline-flex items-center gap-2 mt-8 px-6 py-2.5 rounded-full text-[11px] uppercase transition-shadow hover:shadow-[0_0_24px_rgba(255,215,140,0.35)]"
        style={{
          letterSpacing: '0.22em',
          color: '#ffe2a8',
          border: '1px solid rgba(184,150,79,0.45)',
          background: 'linear-gradient(180deg, #171426, #0c0a18)',
          textDecoration: 'none',
        }}
      >
        <span aria-hidden="true">🌌</span>
        <span>Walk Among the Stars</span>
      </Link>
    </div>
  );
}

export default NeighboringStars;
